import { ListItemIcon, ListItemText } from "@material-ui/core"
import ReportProblemIcon from "@material-ui/icons/ReportProblem"
import * as Sentry from "@sentry/react"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import useAuth from "../auth/useAuth"

interface ReportIssueButtonProps {
  onClick?: () => void
  label?: boolean
  style?: React.CSSProperties
}

const ReportIssueButton: React.FC<ReportIssueButtonProps> = ({
  onClick,
  label = true,
  style
}) => {
  const buttonRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  const OrgName = user?.organisation?.name
  const { t } = useTranslation()
  const iconColor = style?.color ?? "inherit"

  useEffect(() => {
    if (!buttonRef.current) return
    const feedback = Sentry.getFeedback()
    if (!feedback) return

    // Optionally set user email and username in feedback dialog if user is authenticated
    if (user?.id && user?.email && user?.name) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name
      })
      Sentry.setTag("Organisation Name", OrgName)
    }

    // Attach feedback dialog to the button and configure it
    feedback.attachTo(buttonRef.current, {
      colorScheme: "light",
      isNameRequired: true,
      isEmailRequired: true,
      showBranding: false,
      autoInject: true,
      formTitle: t("report_issue.formTitle"),
      nameLabel: t("report_issue.nameLabel"),
      triggerLabel: t("report_issue.triggerLabel"),
      triggerAriaLabel: t("report_issue.triggerAriaLabel"),
      messagePlaceholder: t("report_issue.messagePlaceholder"),
      messageLabel: t("report_issue.messageLabel"),
      isRequiredLabel: t("report_issue.isRequiredLabel"),
      submitButtonLabel: t("report_issue.submitButtonLabel"),
      cancelButtonLabel: t("report_issue.cancelButtonLabel"),
      successMessageText: t("report_issue.successMessageText"),
      addScreenshotButtonLabel: t("report_issue.addScreenshotButtonLabel"),
      removeScreenshotButtonLabel: t("report_issue.removeScreenshotButtonLabel")
    })
  }, [user, t, OrgName])

  const handleClick = () => {
    onClick?.()
  }

  return (
    <div
      ref={buttonRef}
      onClick={handleClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        ...style
      }}
      role="button"
      tabIndex={0}
    >
      {label ? (
        <>
          <ListItemIcon>
            <ReportProblemIcon style={{ color: iconColor }} />
          </ListItemIcon>
          <ListItemText primary={t("Report")} />
        </>
      ) : (
        <ReportProblemIcon style={{ color: iconColor }} />
      )}
    </div>
  )
}

export default ReportIssueButton
